import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type { UpdateMarketPricePayload } from './api';
import type { ListMarketPricesParams } from './schema';

const ROOT_KEY = 'marketPrices';
const listKey = (params: ListMarketPricesParams) =>
  [ROOT_KEY, 'list', params] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useMarketPrices(params: ListMarketPricesParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listMarketPrices(params),
    placeholderData: (prev) => prev, // keep current page visible while next loads
  });
}

export function useUpdateMarketPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMarketPricePayload }) =>
      api.updateMarketPrice(id, payload),
    onSuccess: () => {
      // Invalidate every cached list params combo — server may have re-sorted
      // (modal_price tie-break in the default order) so we can't surgically
      // patch any single page.
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      toast.success('Market price updated');
    },
    onError: (err) => toast.error(describeError(err, 'Failed to update market price')),
  });
}
